/**
 * Error Handler Utilities for Validation Errors
 *
 * This module provides utilities for handling and mapping validation error codes
 * to user-friendly configurations and messages.
 */

// Error Code Constants
export const ERROR_CODES = {
  TERMINAL_OFFLINE: 1001,
  NO_BATTERY_WITH_CABLE: 1004,
  NO_CHARGED_BATTERY: 1005,
  TRANSACTION_EXPIRED: 1078,
  PAYMENT_ERROR: 'PAYMENT_ERROR',
  TERMINAL_SELECTION_ERROR: 'TERMINAL_SELECTION_ERROR'
}

// Error Types
export const ERROR_TYPES = {
  TERMINAL_OFFLINE: 'terminal_offline',
  NO_BATTERY_WITH_CABLE: 'no_battery_with_cable',
  NO_CHARGED_BATTERY: 'no_charged_battery',
  TRANSACTION_EXPIRED: 'transaction_expired',
  PAYMENT_ERROR: 'payment_error',
  TERMINAL_SELECTION_ERROR: 'terminal_selection_error',
  GENERIC: 'generic'
}

/**
 * Determine the error type based on error code
 * @param {number|string} errorCode - The error code from the API or custom code
 * @returns {string} The error type
 */
export function getErrorType(errorCode) {
  switch (errorCode) {
    case ERROR_CODES.TERMINAL_OFFLINE:
      return ERROR_TYPES.TERMINAL_OFFLINE
    case ERROR_CODES.NO_BATTERY_WITH_CABLE:
      return ERROR_TYPES.NO_BATTERY_WITH_CABLE
    case ERROR_CODES.NO_CHARGED_BATTERY:
      return ERROR_TYPES.NO_CHARGED_BATTERY
    case ERROR_CODES.TRANSACTION_EXPIRED:
      return ERROR_TYPES.TRANSACTION_EXPIRED
    case ERROR_CODES.PAYMENT_ERROR:
    case 'PAYMENT_ERROR':
      return ERROR_TYPES.PAYMENT_ERROR
    case ERROR_CODES.TERMINAL_SELECTION_ERROR:
    case 'TERMINAL_SELECTION_ERROR':
      return ERROR_TYPES.TERMINAL_SELECTION_ERROR
    default:
      return ERROR_TYPES.GENERIC
  }
}

/**
 * Get error configuration for display
 * @param {string} errorType - The error type
 * @returns {object} Configuration object with icon, color, and other display properties
 */
export function getErrorConfig(errorType) {
  const configs = {
    [ERROR_TYPES.TERMINAL_OFFLINE]: {
      icon: 'tower-broadcast',
      iconClass: 'text-danger',
      titleKey: 'map_navigation.error_terminal_offline_title',
      messageKey: 'map_navigation.error_terminal_offline_message',
      primaryActionKey: 'map_navigation.error_retry',
      secondaryActionKey: 'map_navigation.error_back_to_token',
      showRetry: true,
      color: '#dc3545'
    },
    [ERROR_TYPES.NO_BATTERY_WITH_CABLE]: {
      icon: 'plug',
      iconClass: 'text-warning',
      titleKey: 'map_navigation.error_no_cable_title',
      messageKey: 'map_navigation.error_no_cable_message',
      primaryActionKey: 'map_navigation.error_select_available_cable',
      secondaryActionKey: 'map_navigation.error_choose_another_battery',
      showCableSelection: true,
      color: '#ffc107'
    },
    [ERROR_TYPES.NO_CHARGED_BATTERY]: {
      icon: 'battery-empty',
      iconClass: 'text-info',
      titleKey: 'map_navigation.error_no_charged_title',
      messageKey: 'map_navigation.error_no_charged_message',
      primaryActionKey: 'map_navigation.error_back_to_map',
      showNearbyTerminals: true,
      color: '#ffc107'
    },
    [ERROR_TYPES.TRANSACTION_EXPIRED]: {
      icon: 'clock',
      iconClass: 'text-warning',
      titleKey: 'map_navigation.validation_error_title',
      messageKey: 'map_navigation.error_transaction_expired',
      primaryActionKey: 'map_navigation.validation_error_close',
      primaryActionEmit: 'secondary',
      showRetry: false,
      color: '#ffc107'
    },
    [ERROR_TYPES.PAYMENT_ERROR]: {
      icon: 'credit-card',
      iconClass: 'text-danger',
      titleKey: 'map_navigation.payment_error_title',
      messageKey: 'map_navigation.payment_error_message',
      primaryActionKey: 'map_navigation.error_choose_another_pay',
      secondaryActionKey: 'map_navigation.validation_error_close',
      primaryActionEmit: 'retry',
      secondaryActionEmit: 'close',
      showRetry: false,
      color: '#dc3545'
    },
    [ERROR_TYPES.TERMINAL_SELECTION_ERROR]: {
      icon: 'exclamation-triangle',
      iconClass: 'text-warning',
      titleKey: 'map_navigation.validation_error_title',
      messageKey: 'map_navigation.terminal_selection_error',
      primaryActionKey: 'map_navigation.error_retry',
      secondaryActionKey: 'map_navigation.validation_error_close',
      showRetry: true,
      color: '#ffc107'
    },
    [ERROR_TYPES.GENERIC]: {
      icon: 'exclamation-circle',
      iconClass: 'text-danger',
      titleKey: 'map_navigation.validation_error_title',
      messageKey: 'map_navigation.generic_error_message',
      primaryActionKey: 'map_navigation.validation_error_close',
      color: '#dc3545'
    }
  }

  return configs[errorType] || configs[ERROR_TYPES.GENERIC]
}

/**
 * Calculate distance between two points using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in kilometers
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371 // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

/**
 * Filter and sort nearby points with available batteries
 * @param {Array} allPoints - All available points
 * @param {object} currentPoint - Current point with geolocation
 * @param {number} maxDistanceKm - Maximum distance in kilometers (default: 0.3km = 300m)
 * @param {number} limit - Maximum number of results (default: 3)
 * @returns {Array} Sorted array of nearby points with distance
 */
export function getNearbyPointsWithBatteries(allPoints, currentPoint, maxDistanceKm = 0.3, limit = 3) {
  if (!currentPoint?.geolocation || !allPoints?.length) {
    return []
  }

  const currentLat = parseFloat(currentPoint.geolocation.lat)
  const currentLon = parseFloat(currentPoint.geolocation.lng)

  if (isNaN(currentLat) || isNaN(currentLon)) {
    return []
  }

  // Filter points with batteries and calculate distances
  const pointsWithDistance = allPoints
    .filter(point => {
      // Exclude current point
      if (point.point_id === currentPoint.point_id) {
        return false
      }

      // Must be online
      if (point.online === false) {
        return false
      }

      // Must have available batteries
      if (!point.available_batteries || parseInt(point.available_batteries) <= 0) {
        return false
      }

      // Must have geolocation
      if (!point.geolocation?.lat || !point.geolocation?.lng) {
        return false
      }

      return true
    })
    .map(point => {
      const pointLat = parseFloat(point.geolocation.lat)
      const pointLon = parseFloat(point.geolocation.lng)
      const distance = calculateDistance(currentLat, currentLon, pointLat, pointLon)

      return {
        ...point,
        distance: distance,
        distanceMeters: Math.round(distance * 1000)
      }
    })
    .filter(point => point.distance <= maxDistanceKm)
    .sort((a, b) => a.distance - b.distance)
    .slice(0, limit)

  return pointsWithDistance
}
